import React, { PropsWithChildren, useEffect, useRef, useState } from "react"
import { deserialize } from "jsonapi-deserializer"
import { useParams } from "react-router-dom"

import consumer from "~/channels/consumer"
import { postEvent } from "~/utils/EventManager"
import { Subscription } from "@rails/actioncable"
import { Cycle, Space } from "~/types"

export const SharedSpaceContext = React.createContext<{
  shareToken?: string
  space: Space
  cycle: Cycle
  switchCycle: () => void
} | null>(null)

export default function SharedSpaceProvider({ children }: PropsWithChildren) {
  const [space, setSpace] = useState<Space | null>(null)
  const [cycle, setCycle] = useState<Cycle>("work")
  const { shareToken } = useParams()
  const subscription = useRef<Subscription | null>(null)

  const switchCycle = () =>
    setCycle((prev) => (prev === "work" ? "break" : "work"))

  useEffect(() => {
    subscription.current = consumer.subscriptions.create(
      { channel: "SpaceChannel", shareToken, join: true },
      {
        connected() {
          postEvent({ shareToken }, "userConnected")
        },

        received(data) {
          const spaceData = deserialize(data["value"])
          if (spaceData.current_timer) {
            if (spaceData.current_timer.break) setCycle("break")
            else setCycle("work")
          }

          if (data.type === "timerEnded") {
            setCycle(data.timer.data.attributes.break ? "work" : "break")
            spaceData.current_timer = null
          }

          setSpace((prev) => ({
            ...prev,
            ...spaceData,
          }))
        },
      },
    )

    return () => {
      subscription?.current?.unsubscribe()
    }
  }, [shareToken])

  if (!space) return null

  return (
    <SharedSpaceContext.Provider
      value={{ shareToken, space, cycle, switchCycle }}
    >
      {children}
    </SharedSpaceContext.Provider>
  )
}
